import { Button, VStack, Input, Textarea, Field, Dialog, CloseButton } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useForm } from 'react-hook-form'
import { useCreateUnion, useUpdateUnion } from '../../hooks/useUnion'
import { ResponsiveModal } from '../shared'
import type { SaveUnionRequest, UnionDetail } from '../../types/union'

interface UnionFormModalProps {
  isOpen: boolean
  onClose: () => void
  union?: UnionDetail
}

export function UnionFormModal({ isOpen, onClose, union }: UnionFormModalProps) {
  const isEdit = !!union
  const createMutation = useCreateUnion()
  const updateMutation = useUpdateUnion(union?.id ?? '')

  const { register, handleSubmit, formState: { isSubmitting } } = useForm<SaveUnionRequest>({
    defaultValues: {
      name: union?.name ?? '',
      localNumber: union?.localNumber ?? '',
      fullName: union?.fullName ?? '',
      description: union?.description ?? '',
      region: union?.region ?? '',
      phone: union?.phone ?? '',
      email: union?.email ?? '',
      website: union?.website ?? '',
    },
  })

  const onSubmit = async (data: SaveUnionRequest) => {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(data)
        toaster.create({ title: 'Union updated', type: 'success', duration: 3000 })
      } else {
        await createMutation.mutateAsync(data)
        toaster.create({ title: 'Union created', type: 'success', duration: 3000 })
      }
      onClose()
    } catch {
      toaster.create({ title: `Failed to ${isEdit ? 'update' : 'create'} union`, type: 'error', duration: 3000 })
    }
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="lg">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Dialog.Header>{isEdit ? 'Edit Union' : 'New Union'}</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>

            <Dialog.Body>
              <VStack gap={4}>
                <Field.Root required>
                  <Field.Label fontSize="sm">Name</Field.Label>
                  <Input size="sm" {...register('name', { required: true })} placeholder="e.g., UFCW" />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Local Number</Field.Label>
                  <Input size="sm" {...register('localNumber')} placeholder="e.g., 75" />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Full Name</Field.Label>
                  <Input size="sm" {...register('fullName')} placeholder="e.g., UFCW Local 75" />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Description</Field.Label>
                  <Textarea size="sm" {...register('description')} rows={2} />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Region</Field.Label>
                  <Input size="sm" {...register('region')} placeholder="e.g., Southwest Ohio" />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Phone</Field.Label>
                  <Input size="sm" {...register('phone')} />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Email</Field.Label>
                  <Input size="sm" type="email" {...register('email')} />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Website</Field.Label>
                  <Input size="sm" {...register('website')} placeholder="https://..." />
                </Field.Root>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
              <Button colorPalette="brand" type="submit" loading={isSubmitting}>
                {isEdit ? 'Save Changes' : 'Create Union'}
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
